/* The English dictionary — every user-visible string in the app, extracted
 * verbatim from the hardcoded literals (the original had NO localization:
 * FlutterFlow multi-language was never enabled, all strings lived inline in
 * the widgets).
 *
 * PARITY WARNING — these strings carry deliberate quirks that MUST survive
 * translation reviews:
 *  - feelsUnsafe ends with a TRAILING SPACE;
 *  - historyTitle uses the U+2019 apostrophe (’), not ASCII ';
 *  - markedSafe ends with the 💞 emoji, no space before it;
 *  - fallbackNameSheet is '- ' (trailing space) vs fallbackNameMap '-';
 *  - timeago strings mirror the Dart timeago package's EnglishMessages. */

export const en = {
  brand: 'Heart2Heart',

  landing: {
    tagline: 'makes it easier to show care and feel safe anywhere, anytime',
    goTestTracking: 'Go Test Tracking',
  },

  tracking: {
    noAlerts: 'No alerts found',
    markedSafe: (name: string) =>
      `${name} has marked themselves safe and stopped sharing their location.💞`,
    /** valueOrDefault fallback on the "safe" screen. */
    fallbackName: 'User',
    /** Map (CustomGMap userName) fallback — no trailing space. */
    fallbackNameMap: '-',
    /** Sheet (ActionsBottomSheet userName) fallback — TRAILING SPACE. */
    fallbackNameSheet: '- ',
  },

  sheet: {
    activatedRow: (when: string) => `Heart2Heart activated ${when}`,
    /** Shown in the activated row when the alerts list is empty. */
    activatedUnknown: '-',
    seeHistory: 'see history',
    feelsUnsafe: (name: string) =>
      `${name}, feels unsafe, let her know that you are aware `,
    sendReassurance: 'Send reassurance',
    getDirections: 'Get directions',
    goBack: 'Go back',
    close: 'Close',
    historyTitle: (name: string) => `${name}’s activation history`,
    historyActivated: (clock: string) => `Heart2Heart activated ${clock}`,
    historyDeactivated: (clock: string) => `Heart2Heart deactivated ${clock}`,
  },

  toasts: {
    reassuranceSent: 'Reassurance sent successfully',
    reassuranceError: 'An error occurred. Please try again.',
    turnOnGeolocation: 'To get directions, turn on geolocation.',
    noRouteForType:
      'There is no route for the selected type. Please select another type.',
  },

  map: {
    /** Hardcoded inside getFirstPointIcon() in the original. */
    alarmActivated: 'Alarm activated',
    /** Spinner accessibility labels. */
    loading: 'Loading',
    loadingRoute: 'Loading route',
  },

  /** Bottom-right badge: `v 0.0.21`. */
  version: (v: string) => `v ${v}`,

  /** _signInOrCreateAccount error toast. */
  authError: (message: string) => `Error: ${message}`,

  /* Dart timeago package EnglishMessages (suffix composed with a space). */
  timeago: {
    aMoment: 'a moment',
    aMinute: 'a minute',
    minutes: (m: number) => `${m} minutes`,
    aboutAnHour: 'about an hour',
    hours: (h: number) => `${h} hours`,
    aDay: 'a day',
    days: (d: number) => `${d} days`,
    aboutAMonth: 'about a month',
    months: (m: number) => `${m} months`,
    aboutAYear: 'about a year',
    years: (y: number) => `${y} years`,
    suffix: (result: string) => `${result} ago`,
  },

  /* convertDistanceToText units. */
  distance: {
    unknown: '- m',
    meters: (n: number) => `${n} m`,
    km: (oneDecimal: string) => `${oneDecimal} km`,
  },
}

export type Dictionary = typeof en
